import React, { useEffect } from "react";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  getRecorderTotalList,
  getRecorderTotalListForCp,
} from "api/recorderAPI";
import {
  Recorder,
  RecorderTotalList,
  RecorderTotalListResponse,
} from "api/interfaces/recorderInterface.interface";
import { TableColumnsType } from "antd";
import { matchStringSearchKeyword } from "utils/functions";
import { SearchVirtualTable } from "./SearchVirtualTable";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";

export type SelectedRowWithAddress = {
  id: string;
  address: string;
};

export type SearchRecorderProps = {
  accountId: string;
  isSingleMode?: boolean;
  onSelectedRowsChange?: (selectedRows: string[]) => void;
  onSelectRowChangeAddress?: (row: SelectedRowWithAddress) => void;
  selectedRow?: string[] | string;
  search: string;
  isDisabled?: boolean;
  userId?: string;
  useRowExpandedSelection?: boolean;
  queryFn?: () => Promise<RecorderTotalListResponse>;
};

export interface DataRow {
  key: string;
  name: string;
  cameras: number;
  location: string;
}

export interface ModalRecorder {
  key: string;
  recorderName: string;
  cameraCount: number;
  location: string;
  brand: string;
  subRows?: ModalRecorder[];
}

/** 저장된 recorderId와 테이블 row key(단일 시스템은 recorderId, 다중은 하위 recorderId)를 맞춤 */
function resolveRecorderListRowKey(
  recorders: ModalRecorder[],
  recorderId: string
): string {
  if (!recorderId || recorders.length === 0) {
    return recorderId;
  }

  const walk = (rows: ModalRecorder[]): string | undefined => {
    for (const row of rows) {
      if (String(row.key) === String(recorderId)) {
        return row.key;
      }
      if (row.subRows?.length) {
        const found = walk(row.subRows);
        if (found) return found;
      }
    }
    return undefined;
  };

  return walk(recorders) ?? recorderId;
}

export function SearchRecorderList(props: SearchRecorderProps): JSX.Element {
  const [recorder, setRecorder] = useState<ModalRecorder[]>([]);
  const [pending, setPending] = useState<boolean>(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const columns: TableColumnsType<ModalRecorder> = useMemo(
    () => [
      {
        key: "recorderName",
        dataIndex: "recorderName",
        title: "Name",
        className: "name",
        render: (value, row) => (
          <>
            {value}
            <address className="location">{row.location}</address>
          </>
        ),
      },
      {
        key: "cameraCount",
        dataIndex: "cameraCount",
        title: "Cameras",
        render: (value, row) => value,
        align: "center",
        width: 80,
      },
    ],
    []
  );
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const queryFn = useMemo(() => {
    if (selectedAccount.accountLevel === "CP") {
      return () => getRecorderTotalListForCp({ accountId: props.accountId });
    }
    return () =>
      getRecorderTotalList({
        accountId: props.accountId,
        userId: props.userId,
      });
  }, [selectedAccount.accountLevel, props.accountId, props.userId]);

  const queryKey = useMemo(() => {
    if (selectedAccount.accountLevel === "CP") {
      return ["recorderCp", props.accountId];
    }
    return ["recorderEu", props.accountId, props.userId];
  }, [selectedAccount.accountLevel, props.accountId, props.userId]);

  const { refetch } = useQuery(queryKey, queryFn, {
    retry: 0,
    refetchOnWindowFocus: false,
    onSuccess: (res: RecorderTotalListResponse) => {
      if (!res.result) {
        return;
      }

      let totalList: ModalRecorder[] = [];
      res.result.forEach((data: RecorderTotalList, index: number) => {
        const isSingleRecorder = data.recorders.length === 1;
        const model: ModalRecorder = {
          key:
            props.isSingleMode && isSingleRecorder
              ? data.recorders[0].recorderId
              : data.mergedSystemId,
          recorderName: data.mergedSystemName,
          cameraCount: data.cameraTotalCount,
          brand: data.brand,
          location: data.location || "",
          subRows: !isSingleRecorder
            ? data.recorders.map((item: Recorder) => ({
                key: item.recorderId,
                recorderName: item.name,
                cameraCount: item.cameraCount,
                brand: item.type,
                location: item.location?.location || "",
              }))
            : [],
        };
        totalList.push(model);
      });

      setRecorder(totalList);
    },
    onError: (e: any) => {},
    onSettled: () => {
      setPending(false);
    },
  });
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows?: ModalRecorder[]
  ) => {
    setSelectedRowKeys(newSelectedRowKeys);

    if (props.isSingleMode) {
      if (
        selectedRows &&
        selectedRows.length !== 0 &&
        props.onSelectRowChangeAddress
      ) {
        props.onSelectRowChangeAddress({
          id: selectedRows[0].key,
          address: selectedRows[0].location,
        });
      }
    } else {
      if (props.onSelectedRowsChange) {
        props.onSelectedRowsChange(newSelectedRowKeys as string[]);
      }
    }
  };

  const data: ModalRecorder[] = useMemo(() => {
    return recorder.filter((rec) => {
      if (props.search) {
        const isFiltering =
          matchStringSearchKeyword(rec.recorderName, props.search) ||
          matchStringSearchKeyword(rec.location, props.search);
        // 하위 recorder 있을때도 keyword 확인
        if (rec.subRows && rec.subRows.length > 0) {
          const tempSubRowsKeyword = rec.subRows.filter((sr) => {
            return (
              matchStringSearchKeyword(sr.recorderName, props.search) ||
              matchStringSearchKeyword(sr.location, props.search)
            );
          });
          return tempSubRowsKeyword.length > 0 || isFiltering;
        }

        return isFiltering;
      } else return true;
    });
  }, [props.search, recorder]);

  useEffect(() => {
    if (
      props.selectedRow === undefined ||
      (typeof props.selectedRow === "string" && props.selectedRow === "")
    ) {
      setSelectedRowKeys([]);
      return;
    }
    if (props.isSingleMode && typeof props.selectedRow === "string") {
      const resolved = resolveRecorderListRowKey(recorder, props.selectedRow);
      setSelectedRowKeys([resolved]);
      return;
    }
    setSelectedRowKeys(props.selectedRow as React.Key[]);
  }, [props.selectedRow, props.isSingleMode, recorder]);

  return (
    <SearchVirtualTable<ModalRecorder>
      columns={columns}
      dataSource={data}
      loading={pending}
      isSingleMode={props.isSingleMode}
      useRowExpandedSelection={props.useRowExpandedSelection}
      rowExpandable={(record) =>
        record.subRows ? record.subRows.length > 1 : false
      }
      expandedRowClassName={(record) =>
        record.subRows ? "expander-children-row" : ""
      }
      selectedRowKeys={selectedRowKeys}
      onSelectChange={onSelectChange}
      checkDisabled={() => props.isDisabled ?? false}
      rowSelectable={(record) => {
        const subRows = record.subRows ?? [];
        if (props.isSingleMode) {
          return subRows.length === 0;
        }
        return true;
      }}
    />
  );
}
